import { BRAND_NAME, DEFINITION, FEATURES, OPERATOR } from "./productFacts";
import type { SeoFaqItem, SeoPlan, SeoTool } from "./sources";
import type { SeoLocale } from "./seoTypes";

/**
 * Pure schema.org builders. Facts only: no AggregateRating, no reviews, no
 * `sameAs` unless a real profile URL is passed in.
 */

type JsonLdNode = Record<string, unknown>;

export interface JsonLdGraph {
  "@context": "https://schema.org";
  "@graph": JsonLdNode[];
}

const ALL_LOCALES: SeoLocale[] = ["cs", "en"];

export function organizationId(origin: string): string {
  return `${origin}/#organization`;
}

export function websiteId(origin: string): string {
  return `${origin}/#website`;
}

export function softwareId(origin: string): string {
  return `${origin}/#software`;
}

export function buildOrganization(origin: string, sameAs: readonly string[] = []): JsonLdNode {
  const node: JsonLdNode = {
    "@type": "Organization",
    "@id": organizationId(origin),
    name: BRAND_NAME,
    legalName: OPERATOR.legalName,
    url: `${origin}/`,
    logo: `${origin}/favicon.png`,
    email: OPERATOR.email,
    vatID: OPERATOR.vatId,
    identifier: { "@type": "PropertyValue", propertyID: "IČO", value: OPERATOR.ico },
    address: { "@type": "PostalAddress", ...OPERATOR.address },
  };
  const profiles = sameAs.filter((url) => /^https:\/\//.test(url));
  if (profiles.length > 0) node.sameAs = profiles;
  return node;
}

export function buildWebSite(origin: string): JsonLdNode {
  return {
    "@type": "WebSite",
    "@id": websiteId(origin),
    name: BRAND_NAME,
    url: `${origin}/`,
    inLanguage: ALL_LOCALES,
    publisher: { "@id": organizationId(origin) },
  };
}

export function buildOffer(plan: SeoPlan, url: string): JsonLdNode {
  // Monthly billing only (UN/CEFACT "MON").
  const unitCode = "MON";
  const offer: JsonLdNode = {
    "@type": "Offer",
    name: plan.name,
    price: plan.price,
    priceCurrency: plan.currency,
    url,
    priceSpecification: {
      "@type": "UnitPriceSpecification",
      price: plan.price,
      priceCurrency: plan.currency,
      billingDuration: plan.billingPeriod,
      unitCode,
    },
  };
  if (plan.description) offer.description = plan.description;
  return offer;
}

export function buildSoftwareApplication(
  origin: string,
  lang: SeoLocale,
  plans: readonly SeoPlan[]
): JsonLdNode {
  const pageUrl = `${origin}/${lang}`;
  const node: JsonLdNode = {
    "@type": "SoftwareApplication",
    "@id": softwareId(origin),
    name: BRAND_NAME,
    url: pageUrl,
    description: DEFINITION[lang],
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "Accounting",
    operatingSystem: "Web",
    inLanguage: ALL_LOCALES,
    featureList: FEATURES[lang],
    image: `${origin}/og/zettl-${lang}.png`,
    publisher: { "@id": organizationId(origin) },
  };
  const valid = plans.filter((p) => Number.isFinite(p.price) && p.price >= 0 && p.name.trim());
  if (valid.length > 0) {
    node.offers = valid.map((plan) => buildOffer(plan, `${pageUrl}#cenik`));
  }
  return node;
}

/** `null` when there are no questions: an empty FAQPage is invalid. */
export function buildFaqPage(url: string, items: readonly SeoFaqItem[]): JsonLdNode | null {
  const valid = items.filter((i) => i.question.trim() && i.answer.trim());
  if (valid.length === 0) return null;
  return {
    "@type": "FAQPage",
    "@id": `${url}#faq`,
    mainEntity: valid.map((item) => ({
      "@type": "Question",
      name: item.question.trim(),
      acceptedAnswer: { "@type": "Answer", text: item.answer.trim() },
    })),
  };
}

/**
 * A free browser-only tool: free (an Offer at 0), runs in any browser,
 * published by the operator. Pure facts from the page itself.
 */
export function buildWebApplication(
  origin: string,
  url: string,
  lang: SeoLocale,
  tool: SeoTool
): JsonLdNode {
  return {
    "@type": "WebApplication",
    "@id": `${url}#app`,
    name: tool.name,
    url,
    description: tool.description,
    applicationCategory: "FinanceApplication",
    operatingSystem: "Any (browser)",
    browserRequirements: "Requires JavaScript",
    isAccessibleForFree: true,
    inLanguage: lang,
    offers: { "@type": "Offer", price: 0, priceCurrency: "CZK" },
    publisher: { "@id": organizationId(origin) },
  };
}

/** The tool page's visible steps as a HowTo; `null` without steps. */
export function buildHowTo(url: string, lang: SeoLocale, tool: SeoTool): JsonLdNode | null {
  const steps = tool.steps.filter((s) => s.name.trim() && s.text.trim());
  if (steps.length === 0) return null;
  return {
    "@type": "HowTo",
    "@id": `${url}#howto`,
    name: tool.howToName,
    inLanguage: lang,
    step: steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name.trim(),
      text: s.text.trim(),
    })),
  };
}

export function buildWebPage(
  origin: string,
  url: string,
  lang: SeoLocale,
  title: string,
  description: string,
  dateModified?: string | null,
  citations: readonly { name: string; url: string }[] = []
): JsonLdNode {
  return {
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: title,
    description,
    inLanguage: lang,
    ...(dateModified ? { dateModified } : {}),
    ...(citations.length > 0
      ? { citation: citations.map((c) => ({ "@type": "CreativeWork", name: c.name, url: c.url })) }
      : {}),
    isPartOf: { "@id": websiteId(origin) },
    about: { "@id": softwareId(origin) },
  };
}

export interface PageJsonLdInput {
  origin: string;
  lang: SeoLocale;
  /** Absolute canonical URL of the page. */
  url: string;
  title: string;
  description: string;
  isLanding: boolean;
  plans?: readonly SeoPlan[];
  faq?: readonly SeoFaqItem[];
  /** A free tool page: adds its WebApplication and HowTo. */
  tool?: SeoTool | null;
  /** The day the content last changed (YYYY-MM-DD), as the page shows it. */
  dateModified?: string | null;
  /** Public sources the page lists as checked: the WebPage's citation. */
  citations?: readonly { name: string; url: string }[];
  sameAs?: readonly string[];
}

/**
 * The full graph for one public page. The landing page carries the product
 * (SoftwareApplication with offers); a page with a visible FAQ (landing,
 * guides) carries its FAQPage; legal pages only describe themselves and point
 * back at the site.
 */
export function buildPageJsonLd(input: PageJsonLdInput): JsonLdGraph {
  const { origin, lang, url, title, description, isLanding } = input;
  const graph: JsonLdNode[] = [
    buildOrganization(origin, input.sameAs),
    buildWebSite(origin),
    buildWebPage(origin, url, lang, title, description, input.dateModified, input.citations),
  ];
  if (isLanding) graph.push(buildSoftwareApplication(origin, lang, input.plans ?? []));
  // A free tool page describes the tool and its steps.
  if (input.tool) {
    graph.push(buildWebApplication(origin, url, lang, input.tool));
    const howTo = buildHowTo(url, lang, input.tool);
    if (howTo) graph.push(howTo);
  }
  // Any page that shows a FAQ (the landing page, the guides) describes it.
  const faq = buildFaqPage(url, input.faq ?? []);
  if (faq) graph.push(faq);
  return { "@context": "https://schema.org", "@graph": graph };
}

/** JSON for a `<script type="application/ld+json">` body: `<` escaped so text can never close the tag. */
export function serializeJsonLd(graph: JsonLdGraph): string {
  return JSON.stringify(graph).replace(/</g, "\\u003c");
}
